import { useEffect, useState } from 'react'
import { useOrderPanelStore } from '../../stores/order-panel-store'
import { supabase } from '../../lib/supabase'

/**
 * Bron van de aanvraag → Lead.LeadSource. Niet-restricted picklist; propageert
 * bij conversie automatisch naar de Opportunity, dus leads én omzet zijn per
 * bron te rapporteren.
 *
 * Opties komen uit GET /api/partners/sources: statische basislijst + actieve
 * partners uit de Supabase-registry (kleda_partners.lead_sources) — een
 * nieuwe partner via /partners verschijnt hier automatisch. De server
 * valideert tegen dezelfde lijst (api/_lib/partner-sources.ts).
 */
// 'Email' (zonder streepje) is de bestaande SF-waarde (1.1k+ leads) — label
// toont 'E-mail' maar de data blijft op één waarde rapporteren.
// Fallback wanneer de fetch faalt — telefonie mag nooit blokkeren. Bevat ook
// de huidige partner-waarden zodat die zelfs bij een registry-hik werken.
const FALLBACK_OPTIES: BronOptie[] = [
  { value: 'Telefoon', label: 'Telefoon' },
  { value: 'Email', label: 'E-mail' },
  { value: 'Webformulier', label: 'Webformulier' },
  { value: 'De Meubelreiniger', label: 'De Meubelreiniger' },
  { value: 'Tapijtreiniging-ploeg', label: 'Tapijtreiniging-ploeg' },
  { value: 'rozenkelim.nl', label: 'Rozenkelim' },
  { value: 'the-weavery.com', label: 'The Weavery' },
]

interface BronOptie {
  value: string
  label: string
}

// Eén fetch per sessie — opties wijzigen zelden en het endpoint cachet
// zelf al ~60s server-side.
let cachedOpties: BronOptie[] | null = null
let inflight: Promise<BronOptie[]> | null = null

async function loadBronOpties(): Promise<BronOptie[]> {
  if (cachedOpties) return cachedOpties
  if (!inflight) {
    inflight = (async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession()
        const token = session?.access_token
        if (!token) return FALLBACK_OPTIES
        const resp = await fetch('/api/partners/sources', {
          headers: { Authorization: `Bearer ${token}` },
        })
        if (!resp.ok) return FALLBACK_OPTIES
        const data = await resp.json()
        const sources = (data?.sources ?? []) as BronOptie[]
        if (!Array.isArray(sources) || sources.length === 0) return FALLBACK_OPTIES
        cachedOpties = sources
        return sources
      } catch {
        return FALLBACK_OPTIES
      } finally {
        inflight = null
      }
    })()
  }
  return inflight
}

export function BronSelector() {
  const leadSource = useOrderPanelStore((s) => s.leadSource)
  const setLeadSource = useOrderPanelStore((s) => s.setLeadSource)
  const [opties, setOpties] = useState<BronOptie[]>(cachedOpties ?? FALLBACK_OPTIES)

  useEffect(() => {
    let mounted = true
    loadBronOpties().then((result) => {
      if (mounted) setOpties(result)
    })
    return () => {
      mounted = false
    }
  }, [])

  return (
    <div className="flex items-center gap-2 ml-auto">
      <label className="text-xs font-medium text-slate-600">Bron</label>
      <select
        value={leadSource}
        onChange={(e) => setLeadSource(e.target.value)}
        className="px-2 py-1.5 border border-slate-200 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
      >
        {opties.map((b) => (
          <option key={b.value} value={b.value}>{b.label}</option>
        ))}
      </select>
    </div>
  )
}
